import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Ban, CheckCircle2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { cn } from '@/shared/lib/cn';
import { formatMoney } from '@/shared/lib/money';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import { ESTADO_ECHEQ_LABEL, listEcheqs, type Echeq } from '@/shared/api/controllers/echeqs';
import { hoyAr } from '@/features/facturacion/plazoNc';
import { diasHasta } from '../lib/promesas';
import { AcreditarEcheqDialog, fechaAr, RechazarEcheqDialog } from './EcheqDialogs';

/**
 * Administración → E-cheqs (AR): la bandeja de los E-cheqs recibidos.
 * "A acreditar" = autorizados (en cartera hasta su fecha de pago, después a
 * cobrar); ahí se acreditan eligiendo el banco o se marcan rechazados por el
 * banco. Pendientes = esperando autorización en Aprobaciones. Historial =
 * acreditados y rechazados.
 */
type Filtro = 'acreditar' | 'pendientes' | 'historial';

const FILTROS: Record<Filtro, { label: string; estados: Echeq['estado'][] }> = {
  acreditar: { label: 'A acreditar', estados: ['Autorizado'] },
  pendientes: { label: 'Pend. autorización', estados: ['Pendiente Autorizacion'] },
  historial: { label: 'Historial', estados: ['Acreditado', 'Rechazado', 'Rechazado Banco', 'Anulado'] },
};

/** Plazo legal para presentar un cheque: 30 días desde su fecha de pago
 *  (server/src/lib/echeqs.ts PLAZO_PRESENTACION_DIAS). */
const PLAZO_PRESENTACION_DIAS = 30;

/** Días que le quedan para presentarlo al cobro (negativo = vencido). */
const diasParaPresentar = (dias: number) => PLAZO_PRESENTACION_DIAS + dias;

/** Ya se puede cobrar y le quedan 5 días o menos para presentarlo (o venció). */
export const presentacionUrgente = (dias: number) => dias <= 0 && diasParaPresentar(dias) <= 5;

/** "Se cobra en 3 días" / "Se cobra hoy" / "Cobrable · vence en 12 días" / "Vencido hace 2 días". */
export function textoCobro(dias: number): string {
  if (dias > 0) return `Se cobra en ${dias} día${dias === 1 ? '' : 's'}`;
  if (dias === 0) return 'Se cobra hoy';
  const resta = diasParaPresentar(dias);
  if (resta < 0) return `Vencido hace ${-resta} día${resta === -1 ? '' : 's'}`;
  return resta === 0 ? 'Cobrable · vence hoy' : `Cobrable · vence en ${resta} día${resta === 1 ? '' : 's'}`;
}

export function EcheqsBandeja({ search, readOnly }: { search: string; readOnly: boolean }) {
  const navigate = useNavigate();
  const { config } = useActiveCountry();
  const q = useQuery({
    // Bajo ['administracion', …]: el botón Actualizar y las acciones ya la invalidan.
    queryKey: ['administracion', config.code, 'echeqs'],
    queryFn: () => listEcheqs(),
    enabled: config.code === 'AR',
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
  });
  const [filtro, setFiltro] = useState<Filtro>('acreditar');
  const [dialog, setDialog] = useState<
    { kind: 'acreditar' | 'rechazo-banco'; echeq: Echeq } | null
  >(null);
  const hoy = hoyAr();

  const filas = useMemo(() => {
    const estados = new Set(FILTROS[filtro].estados);
    const t = search.trim().toLowerCase();
    return (q.data ?? [])
      .filter((e) => estados.has(e.estado))
      .filter(
        (e) =>
          !t ||
          [e.cliente, e.ventaId, e.nroCheque, e.bancoEmisor, e.cuitLibrador, e.vendedor, e.cajaAcreditacion].some(
            (v) => (v ?? '').toLowerCase().includes(t),
          ),
      );
  }, [q.data, filtro, search]);
  const conteo = (f: Filtro) =>
    (q.data ?? []).filter((e) => FILTROS[f].estados.includes(e.estado)).length;
  const total = filas.reduce((a, e) => a + e.monto, 0);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="flex shrink-0 flex-wrap gap-2" role="tablist" aria-label="Filtro de E-cheqs">
        {(Object.keys(FILTROS) as Filtro[]).map((f) => (
          <button
            key={f}
            type="button"
            role="tab"
            aria-selected={filtro === f}
            onClick={() => setFiltro(f)}
            className={cn(
              'rounded-full border px-3 py-1 text-sm font-medium transition-colors',
              filtro === f
                ? 'border-foreground bg-foreground text-background'
                : 'bg-card text-muted-foreground hover:text-foreground',
            )}
          >
            {FILTROS[f].label}
            <span className="ml-1.5 tabular-nums opacity-70">{conteo(f)}</span>
          </button>
        ))}
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border bg-card">
        <div className="min-h-0 flex-1 overflow-auto">
          <table className="w-full min-w-[760px] text-sm">
            <thead className="sticky top-0 z-10 bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3 font-medium">Fecha de pago</th>
                <th className="px-4 py-3 font-medium">Venta</th>
                <th className="px-4 py-3 font-medium">Cheque</th>
                <th className="px-4 py-3 text-right font-medium">Monto</th>
                <th className="px-4 py-3 font-medium">Estado</th>
                <th className="px-4 py-3 font-medium">Vendedor</th>
                {filtro === 'acreditar' && <th className="w-48 px-4 py-3 text-right font-medium">Acciones</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {q.isPending ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">
                    Cargando…
                  </td>
                </tr>
              ) : q.isError ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-destructive">
                    No se pudieron cargar los E-cheqs.
                  </td>
                </tr>
              ) : filas.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">
                    {search.trim() ? 'Sin coincidencias para la búsqueda.' : 'No hay E-cheqs para mostrar.'}
                  </td>
                </tr>
              ) : (
                filas.map((e) => {
                  const dias = diasHasta(e.fechaPago, hoy);
                  const cobrable = dias != null && dias <= 0;
                  return (
                    <tr key={e.id} className="hover:bg-muted/30">
                      <td className="px-4 py-3">
                        <div className="text-foreground">{fechaAr(e.fechaPago)}</div>
                        {e.estado === 'Autorizado' && dias != null && (
                          <div
                            className={cn(
                              'text-xs',
                              !cobrable
                                ? 'text-muted-foreground'
                                : presentacionUrgente(dias)
                                  ? 'font-medium text-rose-700'
                                  : 'font-medium text-amber-700',
                            )}
                          >
                            {textoCobro(dias)}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          className="font-medium text-foreground underline-offset-2 hover:underline"
                          onClick={() =>
                            e.ventaId &&
                            navigate(`/ventas/${e.ventaId}/pagos`, {
                              state: { from: 'administracion' as const, tab: 'echeqs' },
                            })
                          }
                        >
                          {e.ventaId ?? '—'}
                        </button>
                        <div className="text-xs text-muted-foreground">{e.cliente ?? '—'}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-foreground">N° {e.nroCheque}</div>
                        <div className="text-xs text-muted-foreground">
                          {e.bancoEmisor} · CUIT {e.cuitLibrador}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold tabular-nums text-foreground">
                        {formatMoney(e.monto, 'ARS')}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-foreground">
                          {e.estado === 'Autorizado' && cobrable ? 'A cobrar' : ESTADO_ECHEQ_LABEL[e.estado]}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {e.estado === 'Acreditado'
                            ? `${e.cajaAcreditacion ?? '—'} · ${fechaAr(e.fechaAcreditacion)}`
                            : e.motivoRechazo ?? ''}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{e.vendedor ?? '—'}</td>
                      {filtro === 'acreditar' && (
                        <td className="px-4 py-3 text-right">
                          {!readOnly && (
                            <div className="inline-flex gap-1.5">
                              <Button
                                size="sm"
                                className="gap-1.5"
                                onClick={() => setDialog({ kind: 'acreditar', echeq: e })}
                              >
                                <CheckCircle2 className="h-4 w-4" aria-hidden />
                                Acreditar
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                title="Rechazado por el banco"
                                aria-label="Rechazado por el banco"
                                onClick={() => setDialog({ kind: 'rechazo-banco', echeq: e })}
                              >
                                <Ban className="h-4 w-4 text-destructive" aria-hidden />
                              </Button>
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div className="flex shrink-0 items-center justify-between border-t bg-muted/40 px-4 py-2.5 text-sm">
          <span className="text-muted-foreground">
            {filas.length} E-cheq{filas.length === 1 ? '' : 's'}
          </span>
          <span className="font-semibold tabular-nums text-foreground">Total {formatMoney(total, 'ARS')}</span>
        </div>
      </div>

      {dialog?.kind === 'acreditar' && (
        <AcreditarEcheqDialog echeq={dialog.echeq} onClose={() => setDialog(null)} />
      )}
      {dialog?.kind === 'rechazo-banco' && (
        <RechazarEcheqDialog echeq={dialog.echeq} modo="banco" onClose={() => setDialog(null)} />
      )}
    </div>
  );
}
